import { useState, useEffect } from "react";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import config from "../config";
import "../styles/messageTemplateEditor.css";

function MessageTemplateEditor({ isOpen, onClose, onSend, guest, messageType = "invitation", userRole = "Groom" }) {
  const [message, setMessage] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [firebaseTemplate, setFirebaseTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState(() => {
    const saved = localStorage.getItem("whatsappTemplates");
    return saved ? JSON.parse(saved) : [];
  });

  // Default templates with variables
  const defaultTemplates = {
    invitation: `Dear {GUEST_NAME},

You're invited to the wedding of

💍 Chanchala & Kalana 💍

Number of Guests: {GUEST_COUNT}
Sunday, July 12, 2026

👇 View Invitation:
{INVITATION_LINK}`,
    reminder: `Hi {GUEST_NAME}! 📣

This is a friendly reminder about the wedding invitation.

💍 Chanchala & Kalana's Wedding 💍

Date: Sunday, July 12, 2026
Guests: {GUEST_COUNT}

👉 Click here to RSVP:
{INVITATION_LINK}`,
    thankyou: `Hi {GUEST_NAME},

Thank you so much for celebrating with us! 💕

We appreciate you joining us for our special day.

See you soon!

💍 Chanchala & Kalana 💍`
  };

  // Load template from Firebase when editor opens
  useEffect(() => {
    if (isOpen && messageType && guest) {
      loadFirebaseTemplate();
    }
  }, [isOpen, messageType, guest, userRole]);

  const loadFirebaseTemplate = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, "messageTemplates", messageType);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const template = docSnap.data();
        // Try to get role-based template, fallback to default
        const roleContent = template[userRole]?.content || template.content || defaultTemplates[messageType];
        setFirebaseTemplate(template);
        // Show template WITH variables (not replaced)
        setMessage(roleContent);
      } else {
        // If no Firebase template, use default with variables
        setMessage(defaultTemplates[messageType]);
      }
    } catch (error) {
      console.error("Error loading template:", error);
      setMessage(defaultTemplates[messageType] || '');
    } finally {
      setLoading(false);
    }
  };

  const replaceVariables = (template) => {
    const guestName = guest?.name || "Guest";
    const guestTitle = guest?.title || "";
    let displayName = guestTitle ? `${guestTitle} ${guestName}` : guestName;
    
    if (guestTitle === "Mr & Family") {
      displayName = `Mr ${guestName} & Family`;
    }
    
    const guestCount = guest?.numberOfGuests || 1;
    const shortId = guest?.shortId || "";
    const urlId = shortId ? `&id=${shortId}` : "";
    const invitationLink = `${config.invitationBaseUrl}invitation?name=${encodeURIComponent(guestName)}${urlId}`;
    
    return template
      .replace(/{GUEST_NAME}/g, displayName)
      .replace(/{GUEST_COUNT}/g, guestCount)
      .replace(/{INVITATION_LINK}/g, invitationLink);
  };

  const generateDefaultMessage = () => {
    const guestName = guest?.name || "Guest";
    const guestTitle = guest?.title || "";
    let displayName = guestTitle ? `${guestTitle} ${guestName}` : guestName;
    
    if (guestTitle === "Mr & Family") {
      displayName = `Mr ${guestName} & Family`;
    }
    
    const guestCount = guest?.numberOfGuests || 1;
    
    switch(messageType) {
      case "invitation":
        return `Dear ${displayName},

You're invited to the wedding of

💍 Chanchala & Kalana 💍

Number of Guests: ${guestCount}
Sunday, July 12, 2026

👇 View Invitation:
${config.invitationBaseUrl}invitation?name=${encodeURIComponent(guestName)}${guest?.shortId ? `&id=${guest.shortId}` : ""}`;
      
      case "reminder":
        return `Hi ${displayName}! 📣

This is a friendly reminder about the wedding invitation.

💍 Chanchala & Kalana's Wedding 💍

Date: Sunday, July 12, 2026
Guests: ${guestCount}

👉 Click here to RSVP:
${config.invitationBaseUrl}invitation?name=${encodeURIComponent(guestName)}${guest?.shortId ? `&id=${guest.shortId}` : ""}`;
      
      case "thankyou":
        return `Hi ${displayName},

Thank you so much for celebrating with us! 💕

We appreciate you joining us for our special day.

See you soon!

💍 Chanchala & Kalana 💍`;
      
      default:
        return "";
    }
  };



  const handleUseTemplate = (templateType) => {
    if (typeof templateType === 'string' && ['invitation', 'reminder', 'thankyou'].includes(templateType)) {
      loadFirebaseTemplate();
    } else if (typeof templateType === 'string') {
      // Custom saved template
      setMessage(templateType);
    }
  };

  const handleSaveTemplate = () => {
    if (!templateName.trim()) {
      alert("Please enter a template name");
      return;
    }
    
    const newTemplate = {
      id: Date.now(),
      name: templateName,
      content: message,
    };
    
    const updated = [...templates, newTemplate];
    setTemplates(updated);
    localStorage.setItem("whatsappTemplates", JSON.stringify(updated));
    alert("Template saved!");
    setTemplateName("");
  };

  const handleDeleteTemplate = (id) => {
    const updated = templates.filter(t => t.id !== id);
    setTemplates(updated);
    localStorage.setItem("whatsappTemplates", JSON.stringify(updated));
  };

  const handleSendMessage = () => {
    if (!message.trim()) {
      alert("Please enter a message");
      return;
    }
    onSend(message);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="message-editor-overlay">
      <div className="message-editor-modal">
        <div className="editor-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <h3>Edit Message</h3>
            <button className="close-btn" onClick={onClose}>✕</button>
          </div>
          <p style={{ 
            margin: '0.5rem 0 0 0', 
            fontSize: '0.85rem', 
            opacity: 0.9,
            color: 'rgba(255,255,255,0.8)'
          }}>
            💡 Tip: Edit message templates from <a href="/messages" style={{ color: 'white', textDecoration: 'underline' }}>Message Settings</a>
          </p>
        </div>

        <div className="editor-body">
          {loading && (
            <div style={{ padding: '1rem', textAlign: 'center', color: '#667eea' }}>
              Loading template...
            </div>
          )}
          
          {/* Message Textarea */}
          <div className="message-section">
            <label>Message Content:</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Enter your message here..."
              rows="8"
              className="message-textarea"
            />
            <div className="char-count">{message.length} characters</div>
          </div>

          {/* Default Templates */}
          <div className="templates-section">
            <h4>📋 Default Templates:</h4>
            <div className="template-buttons">
              <button 
                className="template-btn"
                onClick={() => handleUseTemplate("invitation")}
              >
                📧 Invitation
              </button>
              <button 
                className="template-btn"
                onClick={() => handleUseTemplate("reminder")}
              >
                🔔 Reminder
              </button>
              <button 
                className="template-btn"
                onClick={() => handleUseTemplate("thankyou")}
              >
                🙏 Thank You
              </button>
            </div>
          </div>

          {/* Save as Template */}
          <div className="save-template-section">
            <h4>💾 Save as Custom Template:</h4>
            <div className="save-template-form">
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="Template name (e.g., 'Birthday Wish')"
                className="template-name-input"
              />
              <button 
                className="save-template-btn"
                onClick={handleSaveTemplate}
              >
                Save Template
              </button>
            </div>
          </div>

          {/* Saved Custom Templates */}
          {templates.length > 0 && (
            <div className="custom-templates-section">
              <h4>⭐ Your Saved Templates:</h4>
              <div className="custom-templates-list">
                {templates.map((template) => (
                  <div key={template.id} className="custom-template-item">
                    <button
                      className="use-custom-template-btn"
                      onClick={() => handleUseTemplate(template.content)}
                      title={template.content}
                    >
                      {template.name}
                    </button>
                    <button
                      className="delete-template-btn"
                      onClick={() => handleDeleteTemplate(template.id)}
                      title="Delete template"
                    >
                      🗑️
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="editor-footer">
          <button 
            className="cancel-btn"
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            className="send-btn"
            onClick={handleSendMessage}
          >
            📱 Send via WhatsApp
          </button>
        </div>
      </div>
    </div>
  );
}

export default MessageTemplateEditor;
